/**
 * Agent React Query Hooks
 * 
 * Provides hooks for agent operations with automatic caching,
 * optimistic updates, and realtime subscriptions.
 * 
 * Part of the 3-layer architecture refactor
 * Repository → Service → React Query Hooks → Components
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { agentService } from '../services';
import type { Agent, AgentStatus, AgentWithQueues } from '../repositories/AgentRepository';

// ==================== QUERY KEYS ====================

export const agentKeys = {
  all: ['agents'] as const,
  current: (userId: string) => [...agentKeys.all, 'current', userId] as const,
  active: () => [...agentKeys.all, 'active'] as const,
};

// ==================== QUERY HOOKS ====================

/**
 * Get current agent info for logged-in user
 * 
 * @example
 * ```tsx
 * const { data: agent, isLoading } = useCurrentAgent(userId);
 * if (agent) {
 *   console.log('Agent:', agent.name, 'Status:', agent.current_status);
 *   console.log('Queues:', agent.queues.map(q => q.queue_name));
 * }
 * ```
 */
export function useCurrentAgent(userId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: agentKeys.current(userId),
    queryFn: () => agentService.getCurrentAgent(userId),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: options?.enabled !== false && !!userId,
  });
}

/**
 * Get all active agents
 * 
 * @example
 * ```tsx
 * const { data: agents = [] } = useActiveAgents();
 * ```
 */
export function useActiveAgents(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: agentKeys.active(),
    queryFn: () => agentService.getActiveAgents(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

// ==================== MUTATION HOOKS ====================

/**
 * Update agent status
 * 
 * Features:
 * - Optimistic updates for instant UI feedback
 * - Automatic cache invalidation
 * - Error handling with rollback
 * 
 * @example
 * ```tsx
 * const updateStatus = useUpdateAgentStatus();
 * 
 * const handleStatusChange = async (status: AgentStatus) => {
 *   await updateStatus.mutateAsync({ userId, status });
 * };
 * ```
 */
export function useUpdateAgentStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: AgentStatus }) =>
      agentService.updateAgentStatus(userId, status),
    onMutate: async ({ userId, status }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: agentKeys.current(userId) });

      // Snapshot the previous value
      const previousAgent = queryClient.getQueryData<AgentWithQueues>(
        agentKeys.current(userId)
      );

      // Optimistically update to the new value
      if (previousAgent) {
        queryClient.setQueryData<AgentWithQueues>(agentKeys.current(userId), {
          ...previousAgent,
          current_status: status,
        });
      }

      return { previousAgent };
    },
    onError: (err, { userId }, context) => {
      // Rollback on error
      if (context?.previousAgent) {
        queryClient.setQueryData(agentKeys.current(userId), context.previousAgent);
      }
      console.error('[useUpdateAgentStatus] Error:', err);
    },
    onSuccess: (updatedAgent, { userId }) => {
      // Update cache with server response
      if (updatedAgent) {
        queryClient.setQueryData(agentKeys.current(userId), (old: AgentWithQueues | undefined) => {
          if (!old) return old;
          return {
            ...old,
            ...updatedAgent,
          };
        });
      }
      // Invalidate active agents list
      queryClient.invalidateQueries({ queryKey: agentKeys.active() });
    },
  });
}

// ==================== REALTIME HOOKS ====================

/**
 * Subscribe to agent status changes from external sources
 * (e.g., status updated from AdminLayout header)
 * 
 * This hook automatically subscribes to realtime updates and
 * updates the React Query cache when changes occur.
 * 
 * @example
 * ```tsx
 * function AgentConsole() {
 *   useAgentStatusSubscription(userId);
 *   const { data: agent } = useCurrentAgent(userId);
 *   // Agent data automatically updates when status changes elsewhere
 * }
 * ```
 */
export function useAgentStatusSubscription(userId: string, enabled = true) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || !userId) return;

    const unsubscribe = agentService.subscribeToStatusChanges(userId, (updatedAgent) => {
      // Update the cache when status changes
      queryClient.setQueryData(agentKeys.current(userId), (old: AgentWithQueues | undefined) => {
        if (!old) return old;
        return {
          ...old,
          ...updatedAgent,
        };
      });

      // Also invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: agentKeys.active() });
    });

    return unsubscribe;
  }, [userId, enabled, queryClient]);
}
